import React, { useState, useEffect, useCallback } from 'react';
import { fetchApi } from '../services/apiBase';
import './PackingList.css';

interface PackingCategory {
  category: string;
  items: string[];
}

interface PackingListProps {
  itineraryId: number;
}

const STORAGE_KEY_PREFIX = 'vizday_packing_';

export function PackingList({ itineraryId }: PackingListProps) {
  const [categories, setCategories] = useState<PackingCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const storageKey = `${STORAGE_KEY_PREFIX}${itineraryId}`;

  // Load checked state from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setCheckedItems(JSON.parse(stored));
      }
    } catch {
      // Ignore parse errors
    }
  }, [storageKey]);

  const toggleItem = useCallback((category: string, item: string) => {
    const key = `${category}:${item}`;
    setCheckedItems(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [storageKey]);

  const fetchPackingList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApi<{
        items: PackingCategory[];
        generated_at: string;
        cached: boolean;
      }>(`/api/v1/planner/itineraries/${itineraryId}/packing-list/`);
      setCategories(data.items);
    } catch (err) {
      console.error('Failed to load packing list:', err);
    } finally {
      setLoading(false);
    }
  }, [itineraryId]);

  if (loading) {
    return <div className="packing-list__loading">Generating packing list...</div>;
  }

  if (categories.length === 0) {
    return (
      <div className="packing-list">
        <button
          className="packing-list__generate-btn"
          onClick={fetchPackingList}
          disabled={loading}
        >
          Generate Packing List
        </button>
      </div>
    );
  }

  return (
    <div className="packing-list">
      <div className="packing-list__header">
        <h3 className="packing-list__title">Packing List</h3>
      </div>
      {categories.map((cat) => (
        <div key={cat.category} className="packing-list__category">
          <h4 className="packing-list__category-name">{cat.category}</h4>
          {cat.items.map((item) => {
            const key = `${cat.category}:${item}`;
            const checked = !!checkedItems[key];
            return (
              <div
                key={key}
                className={`packing-list__item ${checked ? 'packing-list__item--checked' : ''}`}
                onClick={() => toggleItem(cat.category, item)}
              >
                <div className={`packing-list__checkbox ${checked ? 'packing-list__checkbox--checked' : ''}`} />
                <span className="packing-list__item-text">{item}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
